import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ensurePricebookCatalogSync,
  getSkuById,
  MARKETING_ONBOARDING_TO_TIER_SKU,
  MARKETING_TIER_TO_ONBOARDING_SKU,
  PRIMARY_SERVICE_CATEGORY_SET,
  subscribePricebookChanges,
  type ServiceCategory,
} from '../lib/pricebook/pricebook';
import { calcOrder, type CartItemInput } from '../lib/pricing/calcOrder';

const STORAGE_KEY = 'cybard.funnel.cart.v1';

export interface FunnelCartItem {
  readonly skuId: string;
  readonly quantity: number;
}

type FunnelCartContextValue = {
  readonly items: readonly FunnelCartItem[];
  readonly isDrawerOpen: boolean;
  readonly order: ReturnType<typeof calcOrder>;
  readonly totalQuantity: number;
  readonly primaryCategories: readonly ServiceCategory[];
  readonly selectPrimaryTier: (skuId: string) => void;
  readonly replaceItems: (items: readonly FunnelCartItem[]) => void;
  readonly removeSku: (skuId: string) => void;
  readonly clearCart: () => void;
  readonly openDrawer: () => void;
  readonly closeDrawer: () => void;
  readonly toggleDrawer: () => void;
};

const FunnelCartContext = createContext<FunnelCartContextValue | null>(null);

function readPersistedCart(): FunnelCartItem[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        skuId: String(item?.skuId ?? ''),
        quantity: Number(item?.quantity ?? 0),
      }))
      .filter((item) => item.skuId && Number.isInteger(item.quantity) && item.quantity > 0);
  } catch {
    return [];
  }
}

function normalizeCartItems(items: readonly FunnelCartItem[]): FunnelCartItem[] {
  const merged = new Map<string, number>();
  for (const item of items) {
    const skuId = typeof item.skuId === 'string' ? item.skuId.trim() : '';
    const quantity = Number(item.quantity);
    if (!skuId || !Number.isInteger(quantity) || quantity <= 0) continue;
    if (!getSkuById(skuId)) continue;
    merged.set(skuId, (merged.get(skuId) ?? 0) + quantity);
  }

  const primaryByCategory = new Map<ServiceCategory, string>();
  const addOns = new Map<string, number>();
  for (const [skuId, quantity] of merged.entries()) {
    const sku = getSkuById(skuId);
    if (!sku) continue;
    if (sku.isPrimaryService) {
      primaryByCategory.set(sku.serviceCategory, skuId);
      continue;
    }
    if (MARKETING_ONBOARDING_TO_TIER_SKU[skuId]) {
      continue;
    }
    addOns.set(skuId, quantity);
  }

  const normalized: FunnelCartItem[] = [];
  for (const skuId of primaryByCategory.values()) {
    normalized.push({ skuId, quantity: 1 });
  }
  for (const [skuId, quantity] of addOns.entries()) {
    normalized.push({ skuId, quantity });
  }

  const selectedMarketingTierSkuId = primaryByCategory.get('marketing');
  if (selectedMarketingTierSkuId) {
    const onboardingSkuId =
      MARKETING_TIER_TO_ONBOARDING_SKU[selectedMarketingTierSkuId];
    if (onboardingSkuId) {
      normalized.push({ skuId: onboardingSkuId, quantity: 1 });
    }
  }

  normalized.sort((a, b) => a.skuId.localeCompare(b.skuId));
  return normalized;
}

function computePrimaryCategories(items: readonly FunnelCartItem[]): ServiceCategory[] {
  const categories = new Set<ServiceCategory>();
  for (const item of items) {
    const sku = getSkuById(item.skuId);
    if (!sku || !sku.isPrimaryService || sku.billingType !== 'recurring') continue;
    if (!PRIMARY_SERVICE_CATEGORY_SET.has(sku.serviceCategory)) continue;
    categories.add(sku.serviceCategory);
  }
  return Array.from(categories.values());
}

export function FunnelCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<FunnelCartItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const persisted = readPersistedCart();
    setItems(normalizeCartItems(persisted));
  }, []);

  useEffect(() => {
    const unsubscribe = subscribePricebookChanges(() => {
      setItems((previous) => normalizeCartItems(previous));
    });
    void ensurePricebookCatalogSync();
    return unsubscribe;
  }, []);

  useEffect(() => {
    const serializable = items.map((item) => ({
      skuId: item.skuId,
      quantity: item.quantity,
    }));
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  }, [items]);

  const order = useMemo(() => calcOrder(items as CartItemInput[]), [items]);
  const totalQuantity = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );
  const primaryCategories = useMemo(() => computePrimaryCategories(items), [items]);

  const selectPrimaryTier = (skuId: string) => {
    const sku = getSkuById(skuId);
    if (!sku || !sku.isPrimaryService) {
      throw new Error(`Cannot select non-primary sku "${skuId}" as a tier`);
    }

    setItems((prev) => normalizeCartItems([...prev, { skuId, quantity: 1 }]));
  };

  const replaceItems = (nextItems: readonly FunnelCartItem[]) => {
    setItems(normalizeCartItems(nextItems));
  };

  const removeSku = (skuId: string) => {
    setItems((prev) => normalizeCartItems(prev.filter((item) => item.skuId !== skuId)));
  };

  const clearCart = () => setItems([]);
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);
  const toggleDrawer = () => setIsDrawerOpen((value) => !value);

  const value: FunnelCartContextValue = {
    items,
    isDrawerOpen,
    order,
    totalQuantity,
    primaryCategories,
    selectPrimaryTier,
    replaceItems,
    removeSku,
    clearCart,
    openDrawer,
    closeDrawer,
    toggleDrawer,
  };

  return (
    <FunnelCartContext.Provider value={value}>
      {children}
    </FunnelCartContext.Provider>
  );
}

export function useFunnelCart(): FunnelCartContextValue {
  const value = useContext(FunnelCartContext);
  if (!value) {
    throw new Error('useFunnelCart must be used within FunnelCartProvider');
  }
  return value;
}
